
export const FINAL_FEASIBILITY_TABS = { feasibility_data: 1, documents: 2 };
export const COST_BENEFIT_TABS = { final_model: 1, documents: 2 };
export const FISCAL_SUSTAINABILITY_TABS = { ifisa: 1, documents: 2 };
export const RISK_FINALISATION_TABS = { risk_analysis: 1, documents: 2 };
export const ESG_FINAL_CHECK_TABS = { final_check: 1, eia_summary: 2, documents: 3 };
export const PROJECT_COMMUNICATION_TABS = { project_communication: 1, stakeholder_map: 2, documents: 3 };
export const SUMMARY_OF_OPTIONS_TABS = { base_case: 1, options_considered: 2, preferred_option: 3, documents: 4 };
export const INSTITUTIONAL_READINESS_TABS = { readiness: 1, transformation: 2, documents: 3 };
// A step without tabs of its own: its form and its supporting documents.
export const STEP_DOCUMENTS_TABS = { form: 1, documents: 2 };

// The tabs of each tabbed step, in order; Save & Next opens the next tab before the next step.
export const STEP_TAB_IDS: Partial<Record<string, number[]>> = {
    final_feasibility_validation: [FINAL_FEASIBILITY_TABS.feasibility_data, FINAL_FEASIBILITY_TABS.documents],
    cost_benefit_final_model: [COST_BENEFIT_TABS.final_model, COST_BENEFIT_TABS.documents],
    fiscal_sustainability_ifsa: [FISCAL_SUSTAINABILITY_TABS.ifisa, FISCAL_SUSTAINABILITY_TABS.documents],
    risk_finalisation_engine: [RISK_FINALISATION_TABS.risk_analysis, RISK_FINALISATION_TABS.documents],
    long_term_asset_management: [STEP_DOCUMENTS_TABS.form, STEP_DOCUMENTS_TABS.documents],
    esg_final_check: [ESG_FINAL_CHECK_TABS.final_check, ESG_FINAL_CHECK_TABS.eia_summary, ESG_FINAL_CHECK_TABS.documents],
    project_communication_and_stakeholder_engagement_plan: [PROJECT_COMMUNICATION_TABS.project_communication, PROJECT_COMMUNICATION_TABS.stakeholder_map, PROJECT_COMMUNICATION_TABS.documents],
    summary_of_option_considered: [SUMMARY_OF_OPTIONS_TABS.base_case, SUMMARY_OF_OPTIONS_TABS.options_considered, SUMMARY_OF_OPTIONS_TABS.preferred_option, SUMMARY_OF_OPTIONS_TABS.documents],
    institutional_and_operational_readiness: [INSTITUTIONAL_READINESS_TABS.readiness, INSTITUTIONAL_READINESS_TABS.transformation, INSTITUTIONAL_READINESS_TABS.documents],
    financial_requirements_and_funding_strategy: [STEP_DOCUMENTS_TABS.form, STEP_DOCUMENTS_TABS.documents],
    infrastructure_master_plan: [STEP_DOCUMENTS_TABS.form, STEP_DOCUMENTS_TABS.documents],
    project_plan: [STEP_DOCUMENTS_TABS.form, STEP_DOCUMENTS_TABS.documents],
    digital_technology_leverage: [STEP_DOCUMENTS_TABS.form, STEP_DOCUMENTS_TABS.documents],
    economic_multiplier_and_macroeconomics_benefits: [STEP_DOCUMENTS_TABS.form, STEP_DOCUMENTS_TABS.documents],
    economic_and_commercial_viability: [STEP_DOCUMENTS_TABS.form, STEP_DOCUMENTS_TABS.documents],
    risk_statement: [STEP_DOCUMENTS_TABS.form, STEP_DOCUMENTS_TABS.documents],
    supervision_reporting_and_evaluation: [STEP_DOCUMENTS_TABS.form, STEP_DOCUMENTS_TABS.documents],
    implementation_strategy: [STEP_DOCUMENTS_TABS.form, STEP_DOCUMENTS_TABS.documents],
};

// Titles of the tabs of steps that validate one tab at a time, for naming an incomplete tab.
export const STEP_TAB_TITLES: Partial<Record<string, Record<number, string>>> = {
    esg_final_check: {
        [ESG_FINAL_CHECK_TABS.final_check]: 'Social, Environmental & Gender Impact Final Check',
        [ESG_FINAL_CHECK_TABS.eia_summary]: 'Environmental Impact Assessment — Summary of Findings',
    },
    project_communication_and_stakeholder_engagement_plan: {
        [PROJECT_COMMUNICATION_TABS.project_communication]: 'Project Communication',
    },
    institutional_and_operational_readiness: {
        [INSTITUTIONAL_READINESS_TABS.readiness]: 'Readiness Assessment',
    },
    summary_of_option_considered: {
        [SUMMARY_OF_OPTIONS_TABS.base_case]: 'Base Case — Do Nothing / Status Quo',
        [SUMMARY_OF_OPTIONS_TABS.preferred_option]: 'Preferred Option — Selection Justification',
    },
};

// The sub-section and fields a tab validates and saves; a tab without one has nothing to save.
export type StepTabSection<T> = { section: string; fields: (keyof T)[] };

export const ESG_FINAL_CHECK_TAB_SECTIONS: Record<number, StepTabSection<IESGFinalCheck>> = {
    [ESG_FINAL_CHECK_TABS.final_check]: {
        section: 'segi_final_check',
        fields: ['gender_responsive_impact_compliance', 'social_benefit_index', 'environmental_compliance_status', 'sustainability_certification_status'],
    },
    [ESG_FINAL_CHECK_TABS.eia_summary]: {
        section: 'segi_eia_summary_of_findings',
        fields: ['overall_eia_summary_and_environmental_authorisation_status', 'key_environmental_impact_findings', 'key_social_impact_findings', 'gender_impact_findings', 'mitigation_measures_empr_summary', 'residual_impacts_acceptability', 'environmental_authorisation_conditions_compliance_status'],
    },
};

// The Stakeholder Map rows save from their popup; the Documents upload as they are added.
export const PROJECT_COMMUNICATION_TAB_SECTIONS: Record<number, StepTabSection<IProjectCommunicationAndStakeholderEngagementPlan>> = {
    [PROJECT_COMMUNICATION_TABS.project_communication]: {
        section: 'pcsep_project_communication',
        fields: ['investor_engagement_strategies', 'community_participation', 'government_regulatory_interactions', 'collaboration_partner_strategy', 'public_interest_considerations'],
    },
};

// The Options Considered rows save from their popup; the Documents upload as they are added.
export const SUMMARY_OF_OPTIONS_TAB_SECTIONS: Record<number, StepTabSection<ISummaryOfOptionConsidered>> = {
    [SUMMARY_OF_OPTIONS_TABS.base_case]: {
        section: 'soc_base_case',
        fields: ['base_case_description'],
    },
    [SUMMARY_OF_OPTIONS_TABS.preferred_option]: {
        section: 'soc_preferred_option',
        fields: ['preferred_option', 'preferred_option_government_objectives_justification', 'value_for_money_conclusion', 'supporting_documentation_reference'],
    },
};

// The Transformation Strategy fields are optional; the Documents upload as they are added.
export const INSTITUTIONAL_READINESS_TAB_SECTIONS: Record<number, StepTabSection<IInstitutionalAndOperationalReadiness>> = {
    [INSTITUTIONAL_READINESS_TABS.readiness]: {
        section: 'ior_institutional_operational_readiness',
        fields: ['institutional_and_operational_readiness_assessment'],
    },
    [INSTITUTIONAL_READINESS_TABS.transformation]: {
        section: 'ior_institutional_transformation_strategy',
        fields: ['transformation_vision_objectives', 'bbbee_strategy_compliance_plan', 'gender_mainstreaming_youth_development_strategy', 'institutional_capacity_building_skills_development_plan', 'organisational_culture_change_programme', 'procurement_transformation_strategy', 'transformation_monitoring_evaluation_reporting'],
    },
};

// Steps that save one tab at a time, keyed by step: what each tab saves.
export const STEP_TAB_SECTIONS: Partial<Record<string, Record<number, { fields: readonly string[] }>>> = {
    esg_final_check: ESG_FINAL_CHECK_TAB_SECTIONS,
    project_communication_and_stakeholder_engagement_plan: PROJECT_COMMUNICATION_TAB_SECTIONS,
    summary_of_option_considered: SUMMARY_OF_OPTIONS_TAB_SECTIONS,
    institutional_and_operational_readiness: INSTITUTIONAL_READINESS_TAB_SECTIONS,
};

export interface IFinalFeasibilityValidation {
    feasibility_status: string;
    key_constraints_summary: string;
    formAction?: string;
    iscompleted?: boolean;
}

// The Readiness Score points; economic and affordability come from the CBA/CEA and IFISA sections.
export interface IReadinessScoreBreakdown {
    status: number;
    constraints: number;
    economic: number;
    affordability: number;
}

// The Final Feasibility Validation read: the section values (null until saved) and the Readiness Score.
export interface IFinalFeasibilityValidationData {
    feasibility_status: string | null;
    key_constraints_summary: string | null;
    readiness_score: number;
    score_breakdown: IReadinessScoreBreakdown;
}

// CBA when the outcomes are monetisable, CEA when they are not.
export type AnalysisMode = 'cba' | 'cea';

// Numbers are '' until entered; the mode decides which of the CBA/CEA fields apply.
export interface ICostBenefitFinalModel {
    analysis_mode_selector: AnalysisMode;
    total_project_cost: number | '';
    total_economic_benefit: number | '';
    cost_per_outcome_unit: number | '';
    total_outcome_units_delivered: number | '';
    formAction?: string;
    iscompleted?: boolean;
};

// Stage 4 IFISA fields (final_* so they do not clash with the Stage 2 IFISA fields); numbers are '' until entered.
export interface IFiscalSustainabilityIfsa {
    final_fiscal_affordability_index: number | '';
    final_contingent_liability_exposure: string;
    long_term_fiscal_impact_score: number | '';
    formAction?: string;
    iscompleted?: boolean;
};

// The IFISA section read (null until saved).
export interface IFiscalSustainabilityIfsaData {
    final_fiscal_affordability_index: number | null;
    final_contingent_liability_exposure: string | null;
    long_term_fiscal_impact_score: number | null;
}

// The Cost-Benefit Final Model section read (null until saved); the IFISA step's Funding Gap uses it.
export type ICostBenefitFinalModelData = { [K in keyof Omit<ICostBenefitFinalModel, 'formAction' | 'iscompleted'>]: ICostBenefitFinalModel[K] | null };

// Whether the selected environmental_compliance_status option is "Non-Compliant", which blocks the
// stage submission. Matched on the option's label in lowercase letters only, the same rule as the API.
export const isNonCompliantEnvironmentalStatus = (value: string | number | null | undefined, options: { label: string; value: string | number }[]): boolean => {
    const label = options.find(option => option.value === value)?.label ?? '';
    return label.toLowerCase().replace(/[^a-z]/g, '') === 'noncompliant';
};

export interface IRiskFinalisationEngine {
    formAction?: string;
    iscompleted?: boolean;
}

export interface ILongTermAssetManagement {
    expenditure_statement: string;
    funding_statement: string;
    cashflow_statement: string;
    contingent_liability_statement: string;
    formAction?: string;
    iscompleted?: boolean;
}

export interface IFinalInvestmentDecisionEngineCoreOutput {
    formAction?: string;
    iscompleted?: boolean;
}

export interface IESGFinalCheck {
    gender_responsive_impact_compliance: string;
    social_benefit_index: string;
    environmental_compliance_status: string;
    sustainability_certification_status: string;
    social_impact_index: number;
    overall_eia_summary_and_environmental_authorisation_status: string;
    key_environmental_impact_findings: string;
    key_social_impact_findings: string;
    gender_impact_findings: string;
    mitigation_measures_empr_summary: string;
    residual_impacts_acceptability: string;
    environmental_authorisation_conditions_compliance_status: string;
    formAction?: string;
    iscompleted?: boolean;
}
export interface ISupervisionReportingAndEvaluation {
    supervision_framework: string;
    reporting_framework: string;
    evaluation_framework: string;
    me_framework_kpi_data_source: string;
    me_responsibility_feedback_loops: string;
    reporting_frequency_format: string;
    evaluation_schedule: string;
    incentives_for_successful_delivery: string;
    penalties_for_non_performance: string;
    formAction?: string;
    iscompleted?: boolean;
}
export interface IInstitutionalAndOperationalReadiness {
    institutional_and_operational_readiness_assessment: string;
    // Institutional Transformation Strategy (optional).
    transformation_vision_objectives: string;
    bbbee_strategy_compliance_plan: string;
    gender_mainstreaming_youth_development_strategy: string;
    institutional_capacity_building_skills_development_plan: string;
    organisational_culture_change_programme: string;
    procurement_transformation_strategy: string;
    transformation_monitoring_evaluation_reporting: string;
    formAction?: string;
    iscompleted?: boolean;
}

export interface IDigitalTechnologyLeverage {
    digital_technology_leverage: string;
    formAction?: string;
    iscompleted?: boolean;
}

export interface IProjectCommunication {
    investor_engagement_strategies: string;
    community_participation: string;
    government_regulatory_interactions: string;
    collaboration_partner_strategy: string;
    public_interest_considerations: string;
}

export interface IProjectCommunicationAndStakeholderEngagementPlan extends IProjectCommunication {
    formAction?: string;
    iscompleted?: boolean;
}

export interface IInfrastructureMasterPlan {
    master_plan_definition: string;
    formAction?: string;
    iscompleted?: boolean;
}

export interface IProjectPhaseKeyDeliverable {
    deliverable_name: string;
    deliverable_id: number;
    deliverable_owner: string;
    deliverable_date: Date | null;
}
export interface IProjectPhaseMilestone {
    milestone_name: string;
    milestone_id: number;
    milestone_description: string;
    milestone_start_date: Date | null;
    milestone_end_date: Date | null;
    deliverables?: IProjectPhaseKeyDeliverable[];
}

export interface IProjectPhase {
    phase_name: string;
    phase_id: number;
    phase_status: 'not_started' | 'in_progress' | 'completed';
    phase_start_date: Date | null;
    phase_end_date: Date | null;
}
export interface IProjectPlan {
    formAction?: string;
    iscompleted?: boolean;
    phases?: IProjectPhase[];
}

export interface IEconomicAndCommercialViability {
    value_for_money_financial_assessment: string;
    summary_financial_vfm_findings: string;
    benefit_analysis_financial_quantification: string;
    summary_financial_benefit_findings: string;
    risk_evaluation_financial_risk_costing: string;
    summary_financial_risk_findings: string;
    economic_multipliers_financial_returns: string;
    summary_economic_multiplier_financial_findings: string;
    economies_of_scale_whole_life_cost_savings: string;
    summary_economies_of_scale_financial_findings: string;
    cost_effectiveness_unit_cost_metrics: string;
    summary_cea_financial_findings: string;
    social_economic_financial_monetisation: string;
    summary_social_economic_financial_findings: string;
    formAction?: string;
    iscompleted?: boolean;
}

export interface IImplementationStrategy {
    implementation_strategy_overview: string;
    asisa_investment_framework_alignment: string;
    esg_policy_statement_integration_approach: string;
    esg_environmental: string;
    esg_social: string;
    esg_governance: string;
    institutional_investor_funding_mobilisation_strategy: string;
    blended_finance_structure: string;
    institutional_investor_participation_requirements: string;
    due_diligence_framework_standards: string;
    impact_reporting_disclosure_commitments: string;
    impact_measurement_framework: string;
    asisa_disclosure_framework: string;
    stewardship_arrangements_crisa_alignment: string;
    asisa_risk_management_framework: string;
    implementation_plan_phasing_schedule: string;
    implementation_monitoring_evaluation: string;
    exit_strategy_liquidity_provision: string;
    formAction?: string;
    iscompleted?: boolean;
}

export interface IRiskStatement {
    risk_statement_problem_statement: string;
    risk_statement_risk_context: string;
    sensitivity_analysis_overview: string;
    sensitivity_analysis_conclusion: string;
    overall_risk_statement: string;
    formAction?: string;
    iscompleted?: boolean;
}

export interface IEconomicMultiplierAndMacroeconomicsBenefits {
    gdp_contribution_estimate: string;
    regional_economic_growth_effects: string;
    direct_employment: string;
    indirect_employment: string;
    induced_employment: string;
    sector_economic_multiplier: string;
    supply_chain_local_economic_development: string;
    productivity_trade_facilitation: string;
    poverty_reduction_equity_impact: string;
    fiscal_returns_to_government: string;
    sdg_macroeconomic_policy_alignment: string;
    macroeconomic_risks_sensitivity: string;

    formAction?: string;
    iscompleted?: boolean;
}

export interface IFinacialRequirmentsAndFundingstrategy {
    cost_breakdown: string;
    revenue_strategy: string;
    funding_model: string;
    formAction?: string;
    iscompleted?: boolean;
}

export interface ISummaryOfOptionConsidered {
    base_case_description: string;
    preferred_option: number;
    preferred_option_government_objectives_justification: string;
    value_for_money_conclusion: string;
    supporting_documentation_reference: string;
    formAction?: string;
    iscompleted?: boolean;
}

export interface IOptionsConsidered {
    row_id: number;
    option_name: string
    description: string;
    advantages: string;
    disadvantages: string;
    preliminary_cost_estimate: string;
    preliminary_benefits_estimate: string;
    value_for_money_assessment: string;
}

export type IConceptAndViabilityStepTypes =
    IFinalFeasibilityValidation
    | ICostBenefitFinalModel
    | IFiscalSustainabilityIfsa
    | IRiskFinalisationEngine
    | ILongTermAssetManagement
    | IFinalInvestmentDecisionEngineCoreOutput
    | IProjectCommunicationAndStakeholderEngagementPlan
    | IInstitutionalAndOperationalReadiness
    | IDigitalTechnologyLeverage
    | ISupervisionReportingAndEvaluation
    | IESGFinalCheck
    | IInfrastructureMasterPlan
    | IEconomicAndCommercialViability
    | IImplementationStrategy
    | IRiskStatement
    | IEconomicMultiplierAndMacroeconomicsBenefits
    | ISummaryOfOptionConsidered


export interface IApproverStageStatus {
    approver_name: string;
    email: string;
    stg_data_key: string;
    user_id: string;
    stage_status_remarks: string | null;
    dn_stage_status: string;
    dk_stage_status: string;
}


export interface IDecisionData {
    decision_logic_inputs: {
        feasibility: number;
        fiscal_ifsa: number;
        cba_cea: number;
        risk_inv: number;
        social_esg: number;
    };

    // Average likelihood × severity of the Stage 4 risk register (the 0-25 risk score).
    average_risk_score: number;

    investment_readiness_score: number;

    decision: {
        key: "approve" | "conditional" | "rework" | "reject" | string;
        label: string;
        status_text: string;
    };

    decision_options: DecisionOption[];
}

export interface DecisionOption {
    key: string;
    icon: "CheckCircle" | "AlertTriangle" | "RotateCcw" | "XCircle" | string;
    label: string;
    subtitle: string;
    activeClass: string;
    iconColor: string;
    is_active: boolean;
}

export type { IStakeHolder } from '../../common/StakeholderEngagement/types';

export interface IKeyVariablesSensitivity {
    row_id: number;
    variable_assumption: string;
    base_case: number;
    optimistic: number;
    pessimistic: number;
    impact_on_outcome: string;
    key_finding: string;
}